// bedrockMessageHistoryUtils.js  (elastic‑window version)
// -----------------------------------------------------------------------------
// 05‑May‑2025 – Implements LOW/HIGH live window (12‑24) with automatic
// summarise‑on‑overflow.  Every message is raw for 12‑24 turns, then compressed
// into 12‑msg summary blocks stored in DynamoDB.
// -----------------------------------------------------------------------------

// ‑‑ General limits still used elsewhere
export const MAX_DYNAMO_MESSAGES = 80;
export const HARD_MESSAGE_LIMIT  = 80;   // absolute cap when fetching from DB

// ‑‑ Elastic‑window parameters
export const WINDOW_LOW   = 15;          // minimum raw msgs kept verbatim
export const WINDOW_HIGH  = 30;          // when reached → summarise
export const SUMMARY_CHUNK = WINDOW_HIGH - WINDOW_LOW;

// --- helper function to identify meaningful messages
const isMeaningful = (m) =>
  (m.role === 'user' || m.role === 'assistant')
  && !['summary'].includes(m.message_type);

/**
 * Extract text content from a message object
 * @param {Object} message - Message object with possibly complex content
 * @returns {string} - Plain text representation of the message
 */
export function getMessageText(message) {
  if (!message) return '';
  
  if (typeof message.content === 'string') {
    return message.content;
  }
  
  if (Array.isArray(message.content)) {
    return message.content.map(segment => {
      if (typeof segment === 'string') {
        return segment;
      }
      if (segment.type === 'text') {
        return segment.text || '';
      }
      if (segment.type === 'image') {
        return `[Image: ${segment.alt || 'diagram'}]`;
      }
      if (segment.type === 'latex') {
        return `[LaTeX: ${segment.latex || ''}]`;
      }
      return '';
    }).join(' ');
  }
  
  return '';
}

/**
 * Create a name for a conversation from the first few message exchanges
 * @param {Array} messages - Conversation messages
 * @returns {string} - Generated conversation name
 */
export function generateConversationName(messages) {
  if (!messages || messages.length === 0) {
    return 'New Conversation';
  }
  
  // Try to use the first user message as the basis for the name
  const firstUserMessage = messages.find(m => m.role === 'user');
  if (firstUserMessage) {
    const text = getMessageText(firstUserMessage);
    // Truncate and clean up the text
    const maxLength = 30;
    return text.length > maxLength
      ? text.substring(0, maxLength) + '...'
      : text;
  }
  
  return 'Math Tutor Session';
}

/**
 * Format messages from DynamoDB for Bedrock
 * @param {Array} conversationHistory - Array of message items from DynamoDB
 * @returns {Array} - Formatted messages ready for Bedrock
 */
export async function formatDBMessagesForBedrock(conversationHistory) {
  if (!conversationHistory || !Array.isArray(conversationHistory)) {
    return [];
  }

  // Sort messages by timestamp (oldest first)
  const sortedHistory = [...conversationHistory].sort((a, b) => a.timestamp - b.timestamp);
  
  // Extract meta user message if it exists
  const metaItem = sortedHistory.find(item => item.message_type === 'meta');
  
  // Parse the meta_user_message from the content field which is a stringified JSON
  let metaUserMessage = null;
  if (metaItem && metaItem.content) {
    try {
      // Try to parse the stringified JSON content
      const parsedContent = JSON.parse(metaItem.content);
      metaUserMessage = parsedContent.meta_user_message || null;
      console.log("Found meta user message:", metaUserMessage);
    } catch (error) {
      console.warn("Error parsing meta item content:", error);
    }
  }
  
  // If we have a meta user message, create a user message for it
  // This will be included in the LLM conversation history but not shown to the user
  if (metaUserMessage) {
    // Insert it at the beginning of the history
    sortedHistory.unshift({
      role: 'user',
      content: metaUserMessage,
      message_type: 'text',
      timestamp: metaItem.timestamp - 1 // Ensure it comes before other messages
    });
  }
  
  //------------------------------------------------------------------
  // ELASTIC WINDOW SLICE (summaries + last 12-24 raw messages)
  //------------------------------------------------------------------
  const nonMeta   = sortedHistory.filter((m) => m.message_type !== 'meta');
  const summaries = nonMeta.filter((m) => m.message_type === 'summary');
  const raw       = nonMeta.filter((m) => m.message_type !== 'summary');

  // Debug logging for raw message identification
  const extra = raw.length % SUMMARY_CHUNK;      // 0-5  
  // Modified window size calculation logic with added safety buffer
  const windowSz = 
    raw.length <= WINDOW_HIGH
      ? raw.length                   // If we have fewer messages than WINDOW_HIGH, keep them all
      : (extra === 0 
         ? WINDOW_HIGH + 1           // Add safety buffer of +1 to high case
         : WINDOW_LOW + extra + 1);  // Add safety buffer of +1 to partial case
  
  // Ensure we don't exceed array bounds
  const actualWindowSz = Math.min(windowSz, raw.length);
  
  // Fix the discrepancy between windowSz calculation and actual slicing
  const recent = raw.slice(-actualWindowSz);
  const sliceHistory = [...summaries, ...recent];
  console.log(
    `📝 Elastic window in formatter: raw=${raw.length} recent=${recent.length} summaries=${summaries.length}`,
  );

  
  // Keep track of image URLs we've already processed to prevent duplicates
  const processedImageUrls = new Set();

  // Track tool uses to ensure we have corresponding tool results
  // const toolUses = new Map(); // Map<toolUseId, message>
  // const toolResults = new Map(); // Map<toolUseId, message>
  
  // // First pass: identify all tool uses and results
  // sliceHistory.forEach(item => {
  //   // Check for tool_input messages
  //   if (item.message_type === 'tool_input' && item.role === 'assistant') {
  //     try {
  //       let content = item.content;
  //       if (typeof content === 'string') {
  //         content = JSON.parse(content);
  //         console.log('Parsed tool_input and assistant content:', json.stringify(content));
  //       }
        
  //       if (Array.isArray(content)) {
  //         // Find the toolUse segment
  //         const toolUseSegment = content.find(segment => segment.toolUse && segment.toolUse.toolUseId);
  //         if (toolUseSegment) {
  //           toolUses.set(toolUseSegment.toolUse.toolUseId, item);
  //         }
  //       }
  //     } catch (e) {
  //       console.warn('Error parsing tool_input content', e);
  //     }
  //   }
    
  //   // Check for tool_result messages
  //   else if (item.message_type === 'tool_result' && item.role === 'user') {
  //     try {
  //       let content = item.content;
  //       if (typeof content === 'string') {
  //         content = JSON.parse(content);
  //         console.log('Parsed tool_result and user content:', json.stringify(content));
  //       }
        
  //       if (Array.isArray(content)) {
  //         // Find the toolResult segment
  //         const toolResultSegment = content.find(segment => 
  //           segment.toolResult && segment.toolResult.toolUseId);
          
  //         if (toolResultSegment) {
  //           toolResults.set(toolResultSegment.toolResult.toolUseId, item);
  //         }
  //       }
  //     } catch (e) {
  //       console.warn('Error parsing tool_result content', e);
  //     }
  //   }
    
  //   // Also check for tool uses and results in regular messages
  //   else if (typeof item.content === 'string' && item.content.includes('toolUse')) {
  //     try {
  //       const parsed = JSON.parse(item.content);
  //       if (Array.isArray(parsed)) {
  //         console.log('Parsed content string and tooluse:', json.stringify(parsed));
  //         parsed.forEach(segment => {
  //           if (segment.toolUse && segment.toolUse.toolUseId) {
  //             toolUses.set(segment.toolUse.toolUseId, item);
  //           }
  //         });
  //       }
  //     } catch (e) {
  //       // Ignore parsing errors
  //     }
  //   }
    
  //   else if (typeof item.content === 'string' && item.content.includes('toolResult')) {
  //     try {
  //       const parsed = JSON.parse(item.content);
  //       console.log('Parsed content string and toolresult:', json.stringify(parsed));
  //       if (Array.isArray(parsed)) {
  //         parsed.forEach(segment => {
  //           if (segment.toolResult && segment.toolResult.toolUseId) {
  //             toolResults.set(segment.toolResult.toolUseId, item);
  //           }
  //         });
  //       }
  //     } catch (e) {
  //       // Ignore parsing errors
  //     }
  //   }
  // });
  
  // Now format messages for Bedrock
  const formattedMessages = [];
  
  for (const item of sliceHistory) {
    // Always include text messages
    if (item.message_type === 'text') {
      const content = typeof item.content === 'string' ? item.content : JSON.stringify(item.content);
      formattedMessages.push({
        role: item.role,
        content: [{ type: 'text', text: content }]
      });
    }
    
    // // Handle special 'drawing' type messages
    // else if (item.message_type === 'drawing') {
    //   // Extract the image URL and description from the drawing message
    //   const imageUrl = item.imageInfo?.imageUrl;
    //   const imageExplanation = item.imageInfo?.explanation || '';
    //   const description = item.content || 'Student provided a sketch.';
      
    //   // Skip duplicate image references
    //   if (imageUrl && processedImageUrls.has(imageUrl)) {
    //     continue; // Skip this message
    //   }
      
    //   // Add to processed set if we have an image URL
    //   if (imageUrl) {
    //     processedImageUrls.add(imageUrl);
    //   }
      
    //   // Format the drawing message for Bedrock
    //   if (imageExplanation) {
    //     formattedMessages.push({
    //       role: 'user',
    //       content: [
    //         {
    //           type: 'text',
    //           text: `User has sketched something in the drawpad as input to the chat session. They provided the following text along with it as well ${imageExplanation}. Analyzing the sketch...`
    //         },
    //         {
    //           type: 'text',
    //           text: description
    //         }
    //       ]
    //     });
    //   }
    //   formattedMessages.push({
    //     role: 'user',
    //     content: [
    //       {
    //         type: 'text',
    //         text: `User has sketched something in the drawpad as input to the chat session. Analyzing the sketch...`
    //       },
    //       {
    //         type: 'text',
    //         text: 'Drawing content: ' + description
    //       }
    //     ]
    //   });
    // }
    
    // Special handling for image messages
    else if (item.message_type === 'image' && item.imageInfo) {
      // For image messages, create a text-only representation for Bedrock
      const imageUrl = item.imageInfo.imageUrl;
      
      // Skip duplicate image references in close succession
      if (processedImageUrls.has(imageUrl)) {
        continue; // Skip this message
      }
      
      // Add to processed set
      processedImageUrls.add(imageUrl);
      
      const functionStr = item.imageInfo.functionStr || 'mathematical function';
      const textDescription = `Graph of ${functionStr} was generated and shown to the user.`;
      
      formattedMessages.push({
        role: item.role,
        content: [{ 
          type: 'text', 
          text: textDescription
        }]
      });
    }
    
    // Handle tool_input messages
    else if (item.message_type === 'tool_input' && item.role === 'assistant') {
      // Keep the original JSON format to ensure the LLM maintains consistent tool usage patterns
      try {
        // Parse to validate and potentially beautify, but keep the structure
        const parsedContent = JSON.parse(item.content);
        
        // Preserve the tool calling format as is - now without the outer array
        formattedMessages.push({
          role: item.role,
          content: [{ type: 'text', text: JSON.stringify(parsedContent, null, 2) }]
        });
      } catch (e) {
        // If parsing fails, keep the original content
        console.warn('Error parsing tool_input content', e);
        formattedMessages.push({
          role: item.role,
          content: [{ type: 'text', text: item.content }]
        });
      }
    }
    
    // Handle tool_result messages
    else if (item.message_type === 'tool_result' && item.role === 'user') {
      let text = item.content;
      try {
        // Parse the JSON to extract a human-readable description
        // Now expecting a direct object without the outer array
        const parsed = JSON.parse(item.content);
        const result = parsed?.toolResult?.content?.[0]?.json;

        if (result) {
          // Create a natural language description of the result
          if (result.imageUrl) {
            // For graph results
            text = `Tool plotted a graph of ${result.alt || result.functionStr || 'a mathematical function'}`;
            if (result.caption) {
              text += `. ${result.caption}`;
            }
          } else if (result.latex) {
            // For latex results
            text = `Tool generated LaTeX: ${result.latex}`;
          } else if (result.message) {
            // For text results with message
            text = result.message;
          } else {
            // Generic fallback
            text = `Tool returned: ${JSON.stringify(result)}`;
          }
        }
      } catch (e) {
        // If parsing fails, keep the original content
        console.warn('Error parsing tool_result content', e);
      }
      
      // Add a text-only representation of the tool result
      formattedMessages.push({
        role: 'user',
        content: [{ type: 'text', text: text }]
      });
    }
    
    // Handle file upload messages (new!)
    else if (item.message_type === 'file') {
      try {
        // Create a text representation of the file upload
        let extractedText = item.content;
        const fileName = item.fileInfo?.fileName || 'uploaded file';
        const fileType = item.fileInfo?.fileType || 'unknown';
        
        // Format the message content
        const messageText = fileType.startsWith('image/') 
          ? `User uploaded an image (${fileName}). The content was extracted as: ${extractedText}`
          : `User uploaded a file (${fileName}). The extracted content is: ${extractedText}`;
          
        formattedMessages.push({
          role: 'user',
          content: [{ type: 'text', text: messageText }]
        });
      } catch (e) {
        console.warn('Error formatting file upload message', e);
        // Fallback to simple text
        formattedMessages.push({
          role: 'user',
          content: [{ type: 'text', text: `File uploaded: ${item.fileInfo?.fileName || 'unknown file'}` }]
        });
      }
    }
    
    // Handle other JSON content
    else if (typeof item.content === 'string' && (item.content.startsWith('[') || item.content.startsWith('{'))) {
      formattedMessages.push({
        role: item.role,
        content: item.content
      });
    }
    
    // Default case for other message types that are not meta
    else if (item.message_type !== 'meta') {
      // Skip meta messages but include all others
      const content = typeof item.content === 'string' ? item.content : JSON.stringify(item.content);
      
      formattedMessages.push({
        role: item.role,
        content: [{ type: 'text', text: content }]
      });
    }
  }

  return formattedMessages;
}

/**
 * Validate and fix tool use and tool result messages
 * @param {Array} messages - Array of messages to validate
 */
function validateToolUsesAndResults(messages) {
  // Map to track tool use IDs and their corresponding messages
  const toolUses = new Map();
  
  // First pass - collect all tool uses
  messages.forEach((msg, index) => {
    if (msg.role === 'assistant' && Array.isArray(msg.content)) {
      msg.content.forEach(segment => {
        if (segment.toolUse && segment.toolUse.toolUseId) {
          console.log(`Found tool use with ID: ${segment.toolUse.toolUseId} at index ${index}`);
          toolUses.set(segment.toolUse.toolUseId, index);
        }
      });
    }
  });
  
  console.log(`Found ${toolUses.size} tool uses in conversation`);
  
  // Second pass - validate all tool results have corresponding tool uses
  for (let i = messages.length - 1; i >= 0; i--) {
    const msg = messages[i];
    
    // Handle toolResult type segments
    if (msg.role === 'user' && Array.isArray(msg.content)) {
      const toolResultSegments = msg.content.filter(segment => segment.toolResult && segment.toolResult.toolUseId);
      
      // Check each tool result segment
      toolResultSegments.forEach(segment => {
        const toolUseId = segment.toolResult.toolUseId;
        if (!toolUses.has(toolUseId)) {
          console.warn(`Found orphaned tool result with ID ${toolUseId}, looking for missing tool use`);
          
          // Look for a message with message_type = 'tool_input' that might contain our tool use
          for (let j = 0; j < i; j++) {
            const potentialMsg = messages[j];
            if (potentialMsg.role === 'assistant' && Array.isArray(potentialMsg.content)) {
              // Check if this message contains the needed tool use
              if (potentialMsg.content.some(c => c.toolUse && c.toolUse.toolUseId === toolUseId)) {
                // We found the tool use, mark it in our map
                toolUses.set(toolUseId, j);
                console.log(`Found matching tool use at index ${j} for ID ${toolUseId}`);
                break;
              }
            } else if (potentialMsg.role === 'assistant' && potentialMsg.messageType === 'tool_input') {
              // This might be a tool_input message with string content
              try {
                if (typeof potentialMsg.content === 'string') {
                  const parsed = JSON.parse(potentialMsg.content);
                  if (Array.isArray(parsed) && parsed.some(c => c.toolUse && c.toolUse.toolUseId === toolUseId)) {
                    // Found the tool use in a string-serialized message
                    toolUses.set(toolUseId, j);
                    
                    // Replace string content with parsed array
                    messages[j].content = parsed;
                    console.log(`Found and parsed matching tool use at index ${j} for ID ${toolUseId}`);
                    break;
                  }
                }
              } catch (e) {
                // Ignore parsing errors
                console.warn('Error parsing potential tool_input message:', e);
              }
            }
          }
          
          // If we still can't find it, convert this tool result to a text message
          if (!toolUses.has(toolUseId)) {
            console.warn(`Unable to find matching tool use for ID ${toolUseId}, converting to text message`);
            
            // Replace this message with a text-only version
            if (segment.toolResult.content && 
                Array.isArray(segment.toolResult.content) && 
                segment.toolResult.content.length > 0 &&
                segment.toolResult.content[0].json) {
              
              const result = segment.toolResult.content[0].json;
              let resultText = "The previous result was ";
              
              if (result.imageUrl) {
                resultText += `a graph of ${result.alt || result.caption || "a function"}`;
              } else if (result.latex) {
                resultText += `a mathematical expression: ${result.latex}`;
              } else {
                resultText += JSON.stringify(result);
              }
              
              // Replace the tool result with text
              messages[i] = {
                role: 'user',
                content: [{ type: 'text', text: resultText }]
              };
            } else {
              // If we can't make a nice text version, remove the message
              messages.splice(i, 1);
            }
          }
        }
      });
    }
  }
  
  // Third pass - make sure we don't have any orphaned tool uses without results
  const toolResults = new Map();
  messages.forEach((msg, index) => {
    if (msg.role === 'user' && Array.isArray(msg.content)) {
      msg.content.forEach(segment => {
        if (segment.toolResult && segment.toolResult.toolUseId) {
          toolResults.set(segment.toolResult.toolUseId, index);
        }
      });
    }
  });
  
  console.log(`Final message count: ${messages.length}`);
}

function getCompactToolText(msg) {
  if (msg.message_type === 'tool_use') {
    const { name } = msg.toolInfo || {};
    return `[TOOL USE: ${name ?? 'unknown'}]`;
  }
  if (msg.message_type === 'tool_result') {
    const { name } = msg.toolInfo || {};
    return `[TOOL RESULT: ${name ?? 'unknown'} returned]`;
  }
  if (msg.message_type === 'tool_input') {
    const { name } = msg.toolInfo || {};
    return `[TOOL INPUT → ${name ?? 'unknown'}]`;
  }
  return getMessageText(msg);      // fall back to your normal extractor
}

// -----------------------------------------------------------------------------
//  ✱  ELASTIC ROLLING SUMMARY WRITER  ✱
// -----------------------------------------------------------------------------
export async function maybeSummarise(opts) {
  const { conversationId, userId, dynamoUtils, bedrockClient } = opts;
  const meaningful = [...opts.history]
    .sort((a,b)=>a.timestamp-b.timestamp)
    .filter(isMeaningful);

  const summariesSoFar    = opts.history.filter(m => m.message_type === 'summary').length;
  let alreadySummarised   = summariesSoFar * SUMMARY_CHUNK;
  let liveRaw             = meaningful.slice(alreadySummarised);

  console.log(`Checking for summarization: liveRaw.length=${liveRaw.length}, WINDOW_HIGH=${WINDOW_HIGH}`);
  console.log(`Summaries so far: ${summariesSoFar}, alreadySummarised=${alreadySummarised}`);

  // keep compressing until we're back under WINDOW_HIGH
  while (liveRaw.length > WINDOW_HIGH) {
    console.log(`⚠️ Need to summarize! liveRaw.length=${liveRaw.length} > WINDOW_HIGH=${WINDOW_HIGH}`);
    const slice = liveRaw.slice(0, SUMMARY_CHUNK);

    console.log(`Summarizing messages ${alreadySummarised} to ${alreadySummarised+SUMMARY_CHUNK-1}`);
    
    const sliceText = slice
      .map(m => `${m.role.toUpperCase()}: ${getCompactToolText(m)}`)
      .join('\n');
    console.log(`Slice text for summarisation:\n${sliceText}`);
    let summary;
    try {
      summary = await bedrockClient.summariseTextWithHaiku(
        `Summarise this maths-tutoring dialogue between a student and an AI maths tutor. Focus on preserving key outcomes covered, questions, and a high level progress that was achieved.\n\n${sliceText}\n\nSummary:`
      );
    } catch (err) {
      console.error('❌ summariseTextWithHaiku failed, aborting trim:', err);
      return;                             // don't risk data-loss
    }
    console.log(`Summary generated: ${summary}`);

    await dynamoUtils.addMessage({
      conversationId,
      userId,
      messageType: 'summary',
      role: 'user',
      content: summary
    });

    console.log(
      `📝 Elastic summary stored (msgs ${alreadySummarised}-${alreadySummarised+SUMMARY_CHUNK-1})`
    );

    // advance pointers & loop if still overflow
    alreadySummarised  += SUMMARY_CHUNK;
    liveRaw             = meaningful.slice(alreadySummarised);
    console.log(`After summarization: liveRaw.length=${liveRaw.length}, alreadySummarised=${alreadySummarised}`);
  }
}
